package com.eladfinish.bible.contest.ui.components.dialogs

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.text.BasicText
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.LinkAnnotation
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.TextLinkStyles
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.withLink
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.data.common.AssetReader
import com.eladfinish.bible.contest.di.getKoinInstance
import com.eladfinish.bible.contest.ui.components.verticalScrollWithScrollbar
import com.mohamedrejeb.richeditor.model.rememberRichTextState
import com.mohamedrejeb.richeditor.ui.material3.RichTextEditor
import com.mohamedrejeb.richeditor.ui.material3.RichTextEditorDefaults
import dev.icerock.moko.resources.AssetResource
import dev.icerock.moko.resources.StringResource
import dev.icerock.moko.resources.compose.stringResource

sealed class HelpContentType {
    data class Asset(val assetResource: AssetResource) : HelpContentType()
    data class Raw(val content: String) : HelpContentType()
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun GeneralHelpDialog(
    titleResource: StringResource,
    helpContentType: HelpContentType,
    spanStyles: (String) -> List<AnnotatedString.Range<SpanStyle>> = { emptyList() },
    hasClickableParts: Boolean = false,
    isDontShowAgainChecked: Boolean,
    isMarkDown: Boolean = false,
    onApproveDialog: (Boolean) -> Unit,
    onDismiss: () -> Unit,
    onItemClick: (String) -> Unit = {},
) {
    BasicDialogWithTitle(
        onDismiss = onDismiss,
        title = stringResource(resource = titleResource),
        content = {
            Column(
                // fill = false so short help text doesn't force this dialog to expand all the way
                // to BasicDialogWithTitle's heightIn(max = 600.dp) cap — matches that component's
                // own outer wrapper, which already uses fill = false for the same reason. A plain
                // weight(1f) (fill = true by default) makes a weighted child claim the whole
                // available height whenever the incoming constraint has a finite max, even with no
                // minimum — invisible when the help text is long enough to nearly fill 600dp
                // anyway (Search/Browse's help dialogs), but a large empty box under two lines of
                // text on screens with short help content (e.g. Multiple Choice Questions).
                modifier = Modifier
                    .weight(1f, fill = false)
                    .background(Color.White)
                    .fillMaxWidth()
                    .verticalScrollWithScrollbar(),
            ) {
                SelectionContainer {
                    val text = when (helpContentType) {
                        // Read through AssetReader, the one asset path every target implements
                        // (the web target has no other way to read a nested asset).
                        is HelpContentType.Asset -> remember(helpContentType.assetResource) {
                            getKoinInstance<AssetReader>().readFile(helpContentType.assetResource)
                        }
                        is HelpContentType.Raw -> helpContentType.content
                    }

                    val modifier = Modifier
                        .padding(top = 12.dp, bottom = 24.dp)
                        .padding(horizontal = 12.dp)

                    val style = MaterialTheme.typography.bodyLarge.copy(
                        fontSize = 18.sp
                    )

                    if (isMarkDown) {
                        val richTextState = rememberRichTextState()
                        RichTextEditor(
                            modifier = Modifier
                                .fillMaxWidth(),
                            state = richTextState,
                            colors = RichTextEditorDefaults.richTextEditorColors(
                                containerColor = Color.White
                            ),
                        )
                        LaunchedEffect(text) {
                            richTextState.setMarkdown(text)
                        }

                    } else if (hasClickableParts) {
                        val list = spanStyles("") // each item has: start, end, item (SpanStyle)
                        var startPos = 0

                        val annotatedString: AnnotatedString = buildAnnotatedString {
                            list.forEach { range ->
                                // Non-clickable prefix
                                append(text.substring(startPos, range.start))

                                // Clickable segment: make it a real link range
                                val linkText = text.substring(range.start, range.end)
                                withLink(
                                    LinkAnnotation.Clickable(
                                        tag = "click:$linkText",
                                        styles = TextLinkStyles(style = range.item),
                                        linkInteractionListener = { _ ->
                                            onItemClick(linkText)
                                        }
                                    )
                                ) {
                                    append(linkText)
                                }

                                startPos = range.end
                            }

                            // Remaining suffix
                            append(text.substring(startPos, text.length))
                        }

                        BasicText(
                            modifier = modifier,
                            text = annotatedString,
                            style = style,
                        )
                    } else {
                        Text(
                            modifier = modifier,
                            text = AnnotatedString(
                                text = text,
                                spanStyles = spanStyles(text),
                            ),
                            style = style,
                        )
                    }
                }
            }

            var checked: Boolean by rememberSaveable { mutableStateOf(isDontShowAgainChecked) }

            DontShowAgain(
                modifier = Modifier
                    .padding(horizontal = 6.dp),
                checked = checked,
                onCheckedChange = {
                    checked = it
                }
            )

            DialogPositiveButtonInColumn(
                onClick = {
                    onDismiss()
                    onApproveDialog(checked)
                }
            )
        }
    )
}
